// Peças dos Tentos (canvas trilha 8): o grão dourado, o cartão de saldo e os ícones do extrato.
import { useCallback, useEffect, useState } from 'react';
import { cores } from '@truco6/ui';
import { type PainelTentos, type SituacaoTentos, lerTentos } from '../comum/api.ts';
import type { Sessao } from '../comum/conta.ts';
import type { IconeMovimento } from '../comum/tentos.ts';
import { ir } from '../comum/rotas.ts';
import { Icone } from './planos-comum.tsx';

const GRAO = 'M12 2.5c3.6 2.6 5.6 6 5.6 9.5s-2.2 7.2-5.6 9.5c-3.4-2.3-5.6-6-5.6-9.5s2-6.9 5.6-9.5z';

// O Tento: grão dourado com o vinco no meio. Decorativo; o número ao lado sempre diz "Tentos".
export function Tento({ tamanho = 22, brilho = false, vinco = true }: { tamanho?: number; brilho?: boolean; vinco?: boolean }) {
  return (
    <svg className={'tento' + (brilho ? ' brilho' : '')} width={tamanho} height={tamanho} viewBox="0 0 24 24" aria-hidden="true">
      <path d={GRAO} fill={cores.gold400} stroke={cores.gold600} strokeWidth="1.3" />
      {vinco && <path d="M12 6v12" stroke={cores.gold600} strokeWidth="1.2" strokeLinecap="round" />}
    </svg>
  );
}

// Saldo, disponível e reservado, como o servidor manda.
export function linhaDoSaldo(s: SituacaoTentos): string {
  if (s.saldo < 0) return 'Saldo negativo por contestação no cartão';
  return s.reservado > 0 ? `${s.disponivel} disponíveis · ${s.reservado} reservados` : `${s.disponivel} disponíveis`;
}

// Cartão do saldo (Loja, Meu plano e Perfil). `para` diz aonde o botão leva.
export function CartaoSaldo({ situacao, para }: { situacao: SituacaoTentos; para: 'loja' | 'extrato' }) {
  return (
    <section className="cartao-saldo" aria-label={`Seu saldo: ${situacao.saldo} Tentos`}>
      <Tento tamanho={44} brilho />
      <div className="saldo-txt">
        <span className="rotulo-saldo">SEU SALDO</span>
        <span className="saldo-num"><b>{situacao.saldo}</b> Tentos</span>
        <span className="nota">{linhaDoSaldo(situacao)}</span>
        {situacao.bloqueado && <span className="nota aviso-bloqueio">Salas valendo Tentos bloqueadas pelo suporte.</span>}
      </div>
      <button className="chip-saldo" onClick={() => ir({ tela: para })}>
        {para === 'loja' ? 'Loja' : 'Extrato'}<Icone nome="seta" tamanho={16} />
      </button>
    </section>
  );
}

// Carrega o painel de Tentos da API (saldo, extrato e loja).
export function usePainelTentos(sessao: Sessao) {
  const [painel, setPainel] = useState<PainelTentos | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const carregar = useCallback(async () => {
    setErro(null);
    try {
      const p = await lerTentos(sessao);
      setPainel(p);
      return p;
    } catch (e) {
      setErro((e as Error).message);
      return null;
    }
  }, [sessao]);
  useEffect(() => { void carregar(); }, [carregar]);
  return { painel, setPainel, erro, carregar };
}

const ICONES: Record<Exclude<IconeMovimento, 'reservado' | 'plano'>, string> = {
  compra: 'M6 7h12l-1 13H7L6 7zM9 7a3 3 0 0 1 6 0',
  premio: 'M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0V4z',
  ingresso: 'M4 8h16v10H4zM8 8V6h8v2',
  devolvido: 'M9 14L4 9l5-5M4 9h10.5a5.5 5.5 0 0 1 0 11H11',
  ajuste: 'M12 3v3M12 18v3M3 12h3M18 12h3M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8z',
};

// Ícone redondo de cada linha do extrato.
export function IconeDoMovimento({ icone, plano }: { icone: IconeMovimento; plano?: string }) {
  if (icone === 'reservado') {
    return (
      <span className="ico-mov reservado" aria-hidden="true">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="8" /><path d="M12 8v4l3 2" /></svg>
      </span>
    );
  }
  if (icone === 'plano') {
    return (
      <span className={'ico-mov plano plano-' + (plano ?? 'prata')} aria-hidden="true">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.4 6.8 19.1l1-5.8L3.5 9.2l5.9-.9L12 3z" /></svg>
      </span>
    );
  }
  return (
    <span className={'ico-mov ' + icone} aria-hidden="true">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={ICONES[icone]} /></svg>
    </span>
  );
}
